import { BLACK_TEXT, BLUE_600, CORAL_600, GRAY_700, GRAY_800, GRAY_900, YELLOW_600 } from 'constants/styling/theme'

import Collapse from '@mui/material/Collapse'
import { FC } from 'react'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUITooltip } from 'components/common/MUITooltip'
import Stack from '@mui/material/Stack'
import { StarRatingCriteria } from '../StarRatingCriteria'
import { StarRatingDimension } from 'constants/misc'
import { StarRatingDimension as StarRatingDimensionComponent } from '../StarRatingDimension'
import Typography from '@mui/material/Typography'
import { UserAvatarChip } from 'components/common/UserAvatarChip'
import { useStarRatingPopup } from '../_main/StarRatingPopup.context'
import { useTranslation } from 'react-i18next'

/**
 * Composed rating form rendering all dimensions with their criteria/favorite chips.
 * Includes a free-text comment field and a favourite section below it.
 * Reads/writes state from the StarRatingPopup context.
 *
 * @example
 * <StarRatingForm />
 */
export const StarRatingForm: FC = () => {
  const { t } = useTranslation(['star_rating'])

  const {
    hasCreative,
    creativeName,
    creativeAvatarUrl,
    dimensionStates,
    setDimensionRating,
    setDimensionComment,
    toggleCriterion,
    toggleFavorite
  } = useStarRatingPopup()

  const visualsState = dimensionStates[StarRatingDimension.VISUALS]
  const creativeState = dimensionStates[StarRatingDimension.CREATIVE]

  // Whenever the CT is currently favourited, the only available action is to
  // remove — show the "Remove" version of the chip regardless of rating.
  // (At rating < 5 the popup also blocks adding a new favourite — see
  // StarRatingPopup.context: setDimensionRating.)
  const isRemoveFavoriteMode = creativeState.rating != null && creativeState.isFavorite

  // Star-matched badge color for remove-favourite mode: coral (rating ≤ 2),
  // orange (rating 3-4), blue (rating 5 — favourite kept).
  const removeBadgeColor = creativeState.rating != null && creativeState.rating <= 2
    ? 'coral' as const
    : creativeState.rating === 5
      ? 'blue' as const
      : 'orange' as const

  // Heart icon styling — in the default unchecked state we use MUI's outlined
  // variant (FavoriteBorderIcon) so the chip reads as "empty". Filled states
  // use FavoriteIcon with a fill colour matching the current context.
  const isFavoriteUnchecked = !isRemoveFavoriteMode && !creativeState.isFavorite
  const favoriteIconColor = isRemoveFavoriteMode
    ? (removeBadgeColor === 'coral' ? CORAL_600 : removeBadgeColor === 'blue' ? BLUE_600 : YELLOW_600)
    : creativeState.isFavorite
      ? BLUE_600
      : BLACK_TEXT

  const showVisualsCommentField = visualsState.rating !== null
  const showCreativeCommentField = creativeState.rating !== null

  const commentLabelVisuals = visualsState.rating != null && visualsState.rating < 5
    ? t('star_rating:comment_negative')
    : t('star_rating:comment_positive')

  const commentLabelCreative = creativeState.rating != null && creativeState.rating < 5
    ? t('star_rating:comment_negative')
    : creativeName
      ? t('star_rating:comment_positive_named', { name: creativeName })
      : t('star_rating:comment_positive')

  return (
    <Stack gap="2.4rem">

      {hasCreative &&
        <>

          <Stack gap=".4rem">

            <StarRatingDimensionComponent
              label={t('star_rating:dimension.CREATIVE.label')}
              value={creativeState.rating}
              onChange={(val) => setDimensionRating(StarRatingDimension.CREATIVE, val)}
              labelVariant="text-lg"
              labelFontWeight={550}
            />

            <StarRatingCriteria
              rating={creativeState.rating}
              selectedCriteria={creativeState.criteria}
              onToggleCriterion={(c) => toggleCriterion(StarRatingDimension.CREATIVE, c)}
            />

          </Stack>

          <Collapse in={showCreativeCommentField}>
            <Stack gap=".6rem">

              <Typography variant="text-sm" fontWeight={450} color={GRAY_700}>
                {commentLabelCreative}
              </Typography>

              <MUIInputField
                isMultiline
                placeholder={t('star_rating:comment_placeholder')}
                value={creativeState.comment}
                onChange={(e) => setDimensionComment(StarRatingDimension.CREATIVE, e.target.value)}
              />

            </Stack>
          </Collapse>

          <Collapse in={creativeState.rating === 5 || isRemoveFavoriteMode}>
            <Stack
              gap=".8rem"
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '.8rem',
                padding: '1.6rem'
              }}
            >
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap=".4rem">

                <Typography variant="text-md" fontWeight={450} color={GRAY_700}>
                  {t(isRemoveFavoriteMode ? 'star_rating:favorite_remove_prompt_before' : 'star_rating:favorite_prompt_before')}
                </Typography>

                <UserAvatarChip
                  name={creativeName || ''}
                  avatarUrl={creativeAvatarUrl}
                  size="sm"
                  bordered
                  textColor={GRAY_800}
                />

                <Typography variant="text-md" fontWeight={450} color={GRAY_700}>
                  {t(isRemoveFavoriteMode ? 'star_rating:favorite_remove_prompt_after' : 'star_rating:favorite_prompt_after')}
                </Typography>

                <MUITooltip content={t('star_rating:favorite_info_tooltip')}>
                  <InfoOutlinedIcon sx={{ display: 'block', fontSize: '1.6rem', color: GRAY_700, cursor: 'help' }} />
                </MUITooltip>

              </Stack>

              <Stack direction="row">
                <MUITooltip content={t(isRemoveFavoriteMode ? 'star_rating:favorite_remove_hint' : 'star_rating:favorite_hint')}>
                  <MUIBadge
                    label={isRemoveFavoriteMode
                      ? t('star_rating:favorite_label_remove')
                      : creativeState.isFavorite
                        ? t('star_rating:favorite_label_added')
                        : t('star_rating:favorite_label')
                    }
                    variant={creativeState.isFavorite ? 'filled' : 'outlined'}
                    color={creativeState.isFavorite ? 'blue' : 'gray'}
                    icon={
                      isFavoriteUnchecked
                        ? <FavoriteBorderIcon sx={{ fontSize: '1.4rem', color: `${favoriteIconColor} !important` }} />
                        : <FavoriteIcon sx={{ fontSize: '1.4rem', color: `${favoriteIconColor} !important` }} />
                    }
                    rounded
                    onClick={() => toggleFavorite(StarRatingDimension.CREATIVE)}
                    sx={{
                      cursor: 'pointer',
                      paddingX: '0.8rem',
                      '&:hover': { borderColor: BLUE_600 }
                    }}
                    textColor={GRAY_900}
                  />
                </MUITooltip>
              </Stack>
            </Stack>
          </Collapse>

        </>
      }

      <Stack sx={{ mt: hasCreative ? '-1.2rem' : 0 }}>
        <StarRatingDimensionComponent
          label={t('star_rating:dimension.VISUALS.label')}
          value={visualsState.rating}
          onChange={(val) => setDimensionRating(StarRatingDimension.VISUALS, val)}
          labelVariant="text-lg"
          labelFontWeight={550}
        />
      </Stack>

      <Collapse in={showVisualsCommentField}>
        <Stack gap=".6rem">

          <Typography variant="text-sm" fontWeight={450} color={GRAY_700}>
            {commentLabelVisuals}
          </Typography>

          <MUIInputField
            isMultiline
            placeholder={t('star_rating:comment_placeholder')}
            value={visualsState.comment}
            onChange={(e) => setDimensionComment(StarRatingDimension.VISUALS, e.target.value)}
          />

        </Stack>
      </Collapse>

    </Stack>
  )
}
